"use client";

import { formatJobTitleLabel } from "@/lib/job-title";
import React, { useEffect, useState, useSyncExternalStore } from "react";
import { usePathname, useRouter } from "next/navigation";
import { motion, AnimatePresence } from "framer-motion";
import {
  Check,
  Lock,
  LogOut,
  Mail,
  Pencil,
  Shield,
  User,
  X,
} from "lucide-react";
import Sidebar from "./Sidebar";
import AdminSidebar from "./AdminSidebar";
import AppNavbar from "./AppNavbar";
// ModuleSwitcher removed from navbar per design request
import { ToastProvider } from "./ToastProvider";
import { LocaleBoundary, t } from "@/lib/i18n";
import SessionTimeout from "./SessionTimeout";
import { endAuthSession, fetchWithAuth } from "@/lib/api";
import { getAllowedModules, getStoredAllowedModules } from "@/lib/module-access";
import { emitRbacAccessChanged } from "@/lib/use-rbac-access";
import { canAccessAdmin } from "@/lib/admin-access";

type PermissionScope = "all" | "own";

function normalizeStringArray(value: unknown): string[] {
  return Array.isArray(value)
    ? value.filter((item): item is string => typeof item === "string")
    : [];
}

function normalizePermissionScopes(value: unknown): Record<string, PermissionScope> {
  if (!value || typeof value !== "object" || Array.isArray(value)) {
    return {};
  }

  return Object.entries(value).reduce<Record<string, PermissionScope>>(
    (scopes, [permission, scope]) => {
      if (scope === "all" || scope === "own") {
        scopes[permission] = scope;
      }

      return scopes;
    },
    {},
  );
}

function setAuthCookie(name: string, value: string) {
  if (typeof window === "undefined") {
    return;
  }

  const secure = window.location.protocol === "https:" ? "; secure" : "";
  document.cookie = `${name}=${encodeURIComponent(value)}; path=/; max-age=86400; samesite=strict${secure}`;
}

function getProfileFromStorage() {
  if (typeof window === "undefined") {
    return null;
  }

  return {
    name: localStorage.getItem("user_name") || t("Account User"),
    email: localStorage.getItem("user_email") || "user@alita.id",
    role: localStorage.getItem("user_role") || "view_only",
    job_title: localStorage.getItem("user_job_title"),
  };
}

function persistRbacProfile(data: unknown) {
  if (
    typeof window === "undefined" ||
    !data ||
    typeof data !== "object" ||
    Array.isArray(data)
  ) {
    return null;
  }

  const payload = data as Record<string, unknown>;
  const displayRole =
    typeof payload.role === "string" && payload.role.length > 0
      ? payload.role
      : "view_only";
  const rbacRoles = normalizeStringArray(payload.rbac_roles);
  const rbacPermissions = normalizeStringArray(payload.rbac_permissions);
  const rbacPermissionScopes = normalizePermissionScopes(
    payload.rbac_permission_scopes,
  );
  const allowedModules = getAllowedModules(payload.allowed_modules);
  const normalizedUser = {
    ...payload,
    role: displayRole,
    rbac_roles: rbacRoles,
    rbac_permissions: rbacPermissions,
    rbac_permission_scopes: rbacPermissionScopes,
    allowed_modules: allowedModules,
  };

  if (payload.id !== undefined && payload.id !== null) {
    localStorage.setItem("user_id", String(payload.id));
    setAuthCookie("user_id", String(payload.id));
  }

  if (typeof payload.name === "string" && payload.name.length > 0) {
    localStorage.setItem("user_name", payload.name);
  }

  if (typeof payload.email === "string") {
    localStorage.setItem("user_email", payload.email);
    setAuthCookie("user_email", payload.email);
  }

  if (typeof payload.job_title === "string" && payload.job_title.length > 0) {
    localStorage.setItem("user_job_title", payload.job_title);
  } else {
    localStorage.removeItem("user_job_title");
  }

  localStorage.setItem("user_role", displayRole);
  localStorage.setItem("rbac_roles", JSON.stringify(rbacRoles));
  localStorage.setItem("rbac_permissions", JSON.stringify(rbacPermissions));
  localStorage.setItem(
    "rbac_permission_scopes",
    JSON.stringify(rbacPermissionScopes),
  );
  localStorage.setItem("user_allowed_modules", JSON.stringify(allowedModules));
  localStorage.setItem("user_data", JSON.stringify(normalizedUser));

  setAuthCookie("user_role", displayRole);
  setAuthCookie("rbac_roles", JSON.stringify(rbacRoles));
  emitRbacAccessChanged();

  return {
    permissions: rbacPermissions,
    profile: getProfileFromStorage(),
  };
}

const MODULE_THEMES = {
  crm: {
    theme: "light",
    dotClass: "bg-[color:var(--color-primary)]",
  },
  services: {
    theme: "light",
    dotClass: "bg-[color:var(--color-primary)]",
  },
  legal: {
    theme: "light",
    dotClass: "bg-[color:var(--color-primary-hover)]",
  },
  connect: {
    theme: "light",
    dotClass: "bg-[color:var(--color-primary)]",
  },
  procurement: {
    theme: "light",
    dotClass: "bg-[color:var(--color-primary)]",
  },
  insight: {
    theme: "light",
    dotClass: "bg-teal-500",
  },
} as const;

function getWebsiteTitle(pathname: string | null): string {
  const path = pathname || "/";

  if (path.startsWith("/admin")) return "AiBAS - Admin";
  if (path.startsWith("/connect")) return "AiBAS - Connect";
  if (path.startsWith("/crm")) return "AiBAS - CRM";
  if (path.startsWith("/im")) return "AiBAS - IM";
  if (path.startsWith("/legal")) return "AiBAS - Legal";
  if (path.startsWith("/po")) return "AiBAS - PO";
  if (path.startsWith("/procurement")) return "AiBAS - Procurement";
  if (path.startsWith("/insight")) return "AiBAS - Insight";
  if (path.startsWith("/scm")) return "AiBAS - SCM";
  if (path.startsWith("/services")) return "AiBAS - Services";
  if (path.startsWith("/tasks")) return "AiBAS - Tasks";
  if (path.startsWith("/visitor-registration")) {
    return "AiBAS - Visitor Registration";
  }
  if (path.startsWith("/login")) return "AiBAS - Login";

  return "AiBAS";
}

const subscribeToHydration = () => () => undefined;

import { useStoredRbacAccess } from "@/lib/use-rbac-access";

export default function AppShell({ children }: { children: React.ReactNode }) {
  const pathname = usePathname();
  const router = useRouter();
  const { permissions: adminPermissions } = useStoredRbacAccess();

  const isServicesPath = pathname?.startsWith("/services");
  const isLegalPath = pathname?.startsWith("/legal");
  const isConnectPath = pathname?.startsWith("/connect");
  const isAdminPath = pathname?.startsWith("/admin");
  const isProcurementPath = pathname?.startsWith("/procurement");
  const isInsightPath = pathname?.startsWith("/insight");

  let currentModuleKey: keyof typeof MODULE_THEMES = "crm";
  if (isServicesPath) currentModuleKey = "services";
  else if (isLegalPath) currentModuleKey = "legal";
  else if (isConnectPath) currentModuleKey = "connect";
  else if (isProcurementPath) currentModuleKey = "procurement";
  else if (isInsightPath) currentModuleKey = "insight";

  const activeConfig =
    MODULE_THEMES[currentModuleKey as keyof typeof MODULE_THEMES];

  const [isAccountOpen, setIsAccountOpen] = useState(false);
  const [isMobileSidebarOpen, setIsMobileSidebarOpen] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [isPasswordOpen, setIsPasswordOpen] = useState(false);
  const [profile, setProfile] = useState({
    name: t("Account User"),
    email: "user@alita.id",
    role: "view_only",
    job_title: null as string | null,
  });
  const mounted = useSyncExternalStore(
    subscribeToHydration,
    () => true,
    () => false,
  );

  useEffect(() => {
    document.title = getWebsiteTitle(pathname);
  }, [pathname]);

  useEffect(() => {
    // Close the drawer after route navigation, including routes handled by AdminSidebar.
    // eslint-disable-next-line react-hooks/set-state-in-effect
    setIsMobileSidebarOpen(false);
  }, [pathname]);

  useEffect(() => {
    if (!isMobileSidebarOpen) return;

    const previousOverflow = document.body.style.overflow;
    const closeOnEscape = (event: KeyboardEvent) => {
      if (event.key === "Escape") setIsMobileSidebarOpen(false);
    };

    document.body.style.overflow = "hidden";
    window.addEventListener("keydown", closeOnEscape);

    return () => {
      document.body.style.overflow = previousOverflow;
      window.removeEventListener("keydown", closeOnEscape);
    };
  }, [isMobileSidebarOpen]);

  useEffect(() => {
    // Refresh RBAC payload so admin changes take effect without re-login.
    const token = localStorage.getItem("token");
    if (token) {
      fetchWithAuth(`${process.env.NEXT_PUBLIC_API_URL || "/api"}/me`)
        .then((res) => (res.ok ? res.json() : null))
        .then((data) => {
          const refreshed = persistRbacProfile(data);
          if (refreshed && refreshed.profile) {
            setProfile(refreshed.profile);
          }
        })
        .catch(() => {
          /* Silent fail - tidak ganggu UX */
        });
    }
  }, []);

  const theme = "light" as "light" | "dark";
  const canOpenAdminCenter = canAccessAdmin(adminPermissions);

  const [passwordForm, setPasswordForm] = useState({
    current: "",
    next: "",
    confirm: "",
  });

  const [passwordError, setPasswordError] = useState("");
  const [passwordSuccess, setPasswordSuccess] = useState("");

  useEffect(() => {
    const latestProfile = getProfileFromStorage();
    if (!latestProfile) {
      return;
    }

    // eslint-disable-next-line react-hooks/set-state-in-effect
    setProfile((prev) => {
      if (
        prev.name === latestProfile.name &&
        prev.email === latestProfile.email &&
        prev.role === latestProfile.role &&
        prev.job_title === latestProfile.job_title
      ) {
        return prev;
      }

      return latestProfile;
    });
  }, [pathname, adminPermissions]);

  useEffect(() => {
    if (typeof document !== "undefined") {
      document.documentElement.classList.remove("dark");
    }

    if (typeof window !== "undefined") {
      window.localStorage.removeItem("aibas-theme-override");
      window.localStorage.removeItem("aibas-connect-theme");
      window.dispatchEvent(
        new CustomEvent("aibas-theme-changed", { detail: { theme } }),
      );
      window.dispatchEvent(
        new CustomEvent("connect-theme-changed", { detail: { theme } }),
      );
    }
  }, [theme]);

  if (!mounted) return null;

  const initials =
    profile.name
      .split(" ")
      .filter(Boolean)
      .slice(0, 2)
      .map((part) => part[0]?.toUpperCase())
      .join("") || "AU";

  const handleProfileSave = () => {
    if (typeof window !== "undefined") {
      localStorage.setItem("user_name", profile.name);
      localStorage.setItem("user_email", profile.email);
    }
    setIsEditing(false);
  };

  const handleLogout = async () => {
    const ssoLogoutUrl = await endAuthSession();

    setIsAccountOpen(false);
    if (ssoLogoutUrl) {
      window.location.assign(ssoLogoutUrl);
      return;
    }
    router.push("/login");
  };

  const handleLogoClick = () => {
    setIsMobileSidebarOpen(false);
    const allowedModules = getStoredAllowedModules();
    if (allowedModules.length > 0) {
      router.push(`/${allowedModules[0]}`);
      return;
    }

    router.push("/login");
  };

  const handlePasswordSave = async () => {
    setPasswordError("");
    setPasswordSuccess("");

    if (!passwordForm.current || !passwordForm.next) {
      setPasswordError("Please fill in current and new password.");
      return;
    }

    if (passwordForm.next.length < 8) {
      setPasswordError("New password must be at least 8 characters.");
      return;
    }

    if (passwordForm.next !== passwordForm.confirm) {
      setPasswordError("New password and confirmation do not match.");
      return;
    }

    try {
      const res = await fetchWithAuth(
        `${process.env.NEXT_PUBLIC_API_URL || "/api"}/me/password`,
        {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            current_password: passwordForm.current,
            new_password: passwordForm.next,
            new_password_confirmation: passwordForm.confirm,
          }),
        },
      );

      if (!res.ok) {
        const data = await res.json().catch(() => ({}));
        setPasswordError(data.message || "Failed to update password.");
        return;
      }

      setPasswordForm({ current: "", next: "", confirm: "" });
      setPasswordSuccess("Password updated successfully.");
      setIsPasswordOpen(false);
    } catch {
      setPasswordError("Network error. Please try again.");
    }
  };

  // Pages that render before a session exists: the navbar and sidebar call
  // authenticated APIs, and a 401 there sends the browser back to /login.
  const noSidebarPages = [
    "/login",
    "/visitor-registration",
    "/auth/sso-callback",
    "/auth/sso-logout",
  ];
  const isNoSidebarPage = noSidebarPages.some(
    (page) => pathname === page || pathname.startsWith(`${page}/`),
  );
  // Insight share links open for people without the module, so they get no module sidebar.
  const hasApplicationSidebar =
    !isNoSidebarPage && !pathname?.startsWith("/tasks") && !pathname?.startsWith("/insight/s/");

  const MODERN_MODULES = [
    "/tasks", 
    "/crm", 
    "/admin", 
    "/connect", 
    "/im", 
    "/legal", 
    "/ops", 
    "/po", 
    "/procurement",
    "/insight",
    "/scm", 
    "/services"
  ];
  const isModern = MODERN_MODULES.some(route => pathname?.startsWith(route));

  return (
    <div
      className={`${isModern ? "" : "aibas-enterprise"} min-h-[100svh] transition-colors duration-500 ease-in-out overflow-hidden ${
        theme === "dark"
          ? "bg-[color:var(--color-surface-dark)] text-[color:var(--color-text-on-dark)]"
          : `${isModern ? "bg-transparent" : "enterprise-bg"} text-[color:var(--color-text-primary)]`
      }`}
    >
      <ToastProvider>
        <LocaleBoundary>
        <SessionTimeout />

        {!isNoSidebarPage && (
          <AppNavbar
            accountName={profile.name}
            initials={initials}
            isAccountOpen={isAccountOpen}
            isMobileSidebarOpen={isMobileSidebarOpen}
            jobTitle={profile.job_title}
            moduleDotClass={activeConfig.dotClass}
            showSidebarToggle={hasApplicationSidebar}
            theme={theme}
            onAccountToggle={() => {
              setIsMobileSidebarOpen(false);
              setIsAccountOpen((previous) => !previous);
            }}
            onLogoClick={handleLogoClick}
            onSidebarToggle={() => {
              setIsAccountOpen(false);
              setIsMobileSidebarOpen((previous) => !previous);
            }}
          />
        )}

        {/* Global Account Modal (Centered) */}
        <AnimatePresence>
          {isAccountOpen && (
            <div className="fixed inset-0 z-[60] flex items-center justify-center bg-black/60 p-3 backdrop-blur-sm sm:p-4">
              <motion.div
                initial={{ opacity: 0, scale: 0.96, y: 12 }}
                animate={{ opacity: 1, scale: 1, y: 0 }}
                exit={{ opacity: 0, scale: 0.96, y: 12 }}
                className={`relative max-h-[calc(100svh-1.5rem)] w-full max-w-lg overflow-hidden rounded-3xl border shadow-2xl sm:max-h-[90vh] sm:rounded-[32px] ${
                  theme === "dark"
                    ? "border-[color:var(--color-border-dark)] bg-[color:var(--color-surface-dark-elev)]"
                    : "border-[color:var(--color-border)] bg-white"
                }`}
              >
                <button 
                  onClick={() => setIsAccountOpen(false)}
                  className="absolute right-4 top-4 z-10 rounded-full p-2 transition-colors hover:bg-gray-100 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[color:var(--color-primary)] sm:right-6 sm:top-6"
                >
                  <X size={20} className="text-gray-400" />
                </button>

                <div className="relative p-5 sm:p-8">
                  <div className="absolute inset-0 bg-[radial-gradient(circle_at_top,_rgba(229,114,42,0.16),_transparent_60%)] opacity-80" />
                  <div className="relative flex items-center gap-4 sm:gap-6">
                    <div className="flex h-16 w-16 shrink-0 items-center justify-center rounded-2xl bg-gradient-to-tr from-[color:var(--color-primary)] to-[color:var(--color-text-secondary)] text-2xl font-bold text-white shadow-xl sm:h-20 sm:w-20 sm:rounded-3xl sm:text-3xl">
                      {initials}
                    </div>
                    <div>
                      <p
                        className={`text-xl font-black tracking-tight sm:text-2xl ${
                          theme === "dark"
                            ? "text-white"
                            : "text-slate-900"
                        }`}
                      >
                        {t(profile.name) || t("Account User")}
                      </p>
                      <p className="text-sm font-bold text-[color:var(--color-text-secondary)] uppercase tracking-widest mt-1">
                        {formatJobTitleLabel(profile.job_title)}
                      </p>
                    </div>
                  </div>
                </div>

                <div className="max-h-[calc(100svh_-_10rem)] overflow-y-auto px-5 pb-5 custom-scrollbar sm:max-h-[70vh] sm:px-8 sm:pb-8">
                  <div
                    className={`rounded-3xl border p-6 space-y-4 ${
                      theme === "dark"
                        ? "border-[color:var(--color-border-dark)] bg-[color:var(--color-surface-dark-elev)]"
                        : "border-slate-100 bg-slate-50/50"
                    }`}
                  >
                    <div className="flex items-center justify-between mb-2">
                      <p
                        className={`text-[10px] uppercase tracking-[0.2em] font-black ${
                          theme === "dark"
                            ? "text-[color:var(--color-text-on-dark-muted)]"
                            : "text-slate-400"
                        }`}
                      >
                        {t("Personal Information")}
                      </p>
                      <button
                        type="button"
                        onClick={() => setIsEditing((prev) => !prev)}
                        className={`text-xs font-bold flex items-center gap-2 px-4 py-1.5 rounded-full transition ${
                          theme === "dark"
                            ? "bg-slate-800 text-white"
                            : "bg-white text-slate-600 shadow-sm border border-slate-200 hover:bg-slate-50"
                        }`}
                      >
                        {isEditing ? (
                          <>
                            <X size={12} /> {t("Cancel")}
                          </>
                        ) : (
                          <>
                            <Pencil size={12} /> {t("Edit Profile")}
                          </>
                        )}
                      </button>
                    </div>

                    <div className="space-y-4">
                      <div>
                        <label className="text-[10px] font-bold uppercase tracking-wider text-slate-400 block mb-1.5">
                          {t("Full Name")}
                        </label>
                        <div className="flex items-center gap-3">
                          <User
                            size={16}
                            className="text-[color:var(--color-primary)]"
                          />
                          <input
                            type="text"
                            value={profile.name}
                            onChange={(e) =>
                              setProfile((prev) => ({
                                ...prev,
                                name: e.target.value,
                              }))
                            }
                            disabled={!isEditing}
                            className={`w-full bg-transparent text-sm font-bold outline-none transition-all ${
                              theme === "dark"
                                ? "text-white"
                                : "text-slate-900"
                            } ${
                              isEditing
                                ? "border-b-2 border-[color:var(--color-primary)] pb-1"
                                : "border-b-2 border-transparent"
                            }`}
                            placeholder={t("Your name")}
                          />
                        </div>
                      </div>

                      <div>
                        <label className="text-[10px] font-bold uppercase tracking-wider text-slate-400 block mb-1.5">
                          {t("Email Address")}
                        </label>
                        <div className="flex items-center gap-3">
                          <Mail
                            size={16}
                            className="text-[color:var(--color-primary)]"
                          />
                          <input
                            type="email"
                            value={profile.email}
                            onChange={(e) =>
                              setProfile((prev) => ({
                                ...prev,
                                email: e.target.value,
                              }))
                            }
                            disabled={!isEditing}
                            className={`w-full bg-transparent text-sm font-bold outline-none transition-all ${
                              theme === "dark"
                                ? "text-white"
                                : "text-slate-900"
                            } ${
                              isEditing
                                ? "border-b-2 border-[color:var(--color-primary)] pb-1"
                                : "border-b-2 border-transparent"
                            }`}
                            placeholder={t("email@alita.id")}
                          />
                        </div>
                      </div>
                    </div>
                  </div>

                  <div
                    className={`mt-4 rounded-3xl border p-6 space-y-4 ${
                      theme === "dark"
                        ? "border-[color:var(--color-border-dark)] bg-[color:var(--color-surface-dark-elev)]"
                        : "border-slate-100 bg-white shadow-sm"
                    }`}
                  >
                    <div className="flex items-center justify-between">
                      <p
                        className={`text-[10px] uppercase tracking-[0.2em] font-black ${
                          theme === "dark"
                            ? "text-[color:var(--color-text-on-dark-muted)]"
                            : "text-slate-400"
                        }`}
                      >
                        {t("Security Settings")}
                      </p>
                      <button
                        type="button"
                        onClick={() => setIsPasswordOpen((prev) => !prev)}
                        className={`text-xs font-bold flex items-center gap-2 px-4 py-1.5 rounded-full transition ${
                          theme === "dark"
                            ? "bg-slate-800 text-white"
                            : "bg-slate-50 text-slate-600 border border-slate-200"
                        }`}
                      >
                        <Lock size={12} />
                        {isPasswordOpen ? t("Hide") : t("Change Password")}
                      </button>
                    </div>

                    {isPasswordOpen && (
                      <div className="space-y-4 pt-2">
                        <input
                          type="password"
                          placeholder={t("Current password")}
                          value={passwordForm.current}
                          onChange={(e) =>
                            setPasswordForm((prev) => ({
                              ...prev,
                              current: e.target.value,
                            }))
                          }
                          className={`w-full rounded-2xl px-4 py-3 text-sm font-bold outline-none transition-all ${
                            theme === "dark"
                              ? "bg-slate-800 text-white border border-slate-700 focus:border-[color:var(--color-primary)]"
                              : "bg-slate-50 text-slate-900 border border-slate-200 focus:border-[color:var(--color-primary)]"
                          }`}
                        />
                        <input
                          type="password"
                          placeholder={t("New password")}
                          value={passwordForm.next}
                          onChange={(e) =>
                            setPasswordForm((prev) => ({
                              ...prev,
                              next: e.target.value,
                            }))
                          }
                          className={`w-full rounded-2xl px-4 py-3 text-sm font-bold outline-none transition-all ${
                            theme === "dark"
                              ? "bg-slate-800 text-white border border-slate-700 focus:border-[color:var(--color-primary)]"
                              : "bg-slate-50 text-slate-900 border border-slate-200 focus:border-[color:var(--color-primary)]"
                          }`}
                        />
                        <input
                          type="password"
                          placeholder={t("Confirm new password")}
                          value={passwordForm.confirm}
                          onChange={(e) =>
                            setPasswordForm((prev) => ({
                              ...prev,
                              confirm: e.target.value,
                            }))
                          }
                          className={`w-full rounded-2xl px-4 py-3 text-sm font-bold outline-none transition-all ${
                            theme === "dark"
                              ? "bg-slate-800 text-white border border-slate-700 focus:border-[color:var(--color-primary)]"
                              : "bg-slate-50 text-slate-900 border border-slate-200 focus:border-[color:var(--color-primary)]"
                          }`}
                        />
                        {passwordError && (
                          <p className="text-xs font-bold text-rose-500">
                            {passwordError}
                          </p>
                        )}
                        {passwordSuccess && (
                          <p className="text-xs font-bold text-emerald-500">
                            {passwordSuccess}
                          </p>
                        )}
                        <div className="flex justify-end">
                          <button
                            type="button"
                            onClick={handlePasswordSave}
                            className="inline-flex items-center gap-2 px-6 py-2.5 text-xs font-black rounded-full bg-gradient-to-r from-[color:var(--color-primary)] to-[#cc5f20] text-white shadow-lg shadow-[color:var(--color-primary)]/25 hover:brightness-110 active:scale-95 transition-all"
                          >
                            <Check size={14} /> {t("UPDATE PASSWORD")}
                          </button>
                        </div>
                      </div>
                    )}
                  </div>

                  <div className="mt-8 flex flex-col gap-3">
                    {isEditing && (
                      <button
                        type="button"
                        onClick={handleProfileSave}
                        className="w-full flex items-center justify-center gap-2 px-6 py-3 text-sm font-black rounded-full bg-gradient-to-r from-[color:var(--color-primary)] to-[#cc5f20] text-white shadow-xl shadow-[color:var(--color-primary)]/20 hover:brightness-110 active:scale-95 transition-all"
                      >
                        <Check size={18} /> {t("SAVE CHANGES")}
                      </button>
                    )}

                    {canOpenAdminCenter && (
                      <button
                        type="button"
                        onClick={() => {
                          setIsAccountOpen(false);
                          router.push("/admin");
                        }}
                        className={`w-full flex items-center justify-center gap-2 px-6 py-3 rounded-full text-xs font-black transition-all border-2 ${
                          theme === "dark"
                            ? "bg-orange-500/10 text-orange-400 border-orange-500/30 hover:bg-orange-500/20"
                            : "bg-orange-50 text-orange-600 border-orange-100 hover:bg-orange-100"
                        }`}
                      >
                        <Shield size={16} /> {t("GO TO ADMIN CENTER")}
                      </button>
                    )}

                    <button
                      type="button"
                      onClick={handleLogout}
                      className={`w-full flex items-center justify-center gap-2 px-6 py-3 rounded-full text-xs font-black transition-all ${
                        theme === "dark"
                          ? "bg-slate-800 text-white hover:bg-slate-700"
                          : "bg-slate-100 text-slate-600 hover:bg-slate-200"
                      }`}
                    >
                      <LogOut size={16} /> {t("LOG OUT")}
                    </button>
                  </div>
                </div>
              </motion.div>
            </div>
          )}
        </AnimatePresence>

        <AnimatePresence>
          {hasApplicationSidebar && isMobileSidebarOpen && (
            <>
              <motion.button
                type="button"
                aria-label={t("Close navigation menu")}
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
                onClick={() => setIsMobileSidebarOpen(false)}
                className="fixed inset-x-0 bottom-0 top-14 z-30 bg-slate-950/45 backdrop-blur-[2px] sm:top-16 xl:hidden"
              />
              <motion.div
                id="mobile-app-sidebar"
                role="dialog"
                aria-modal="true"
                aria-label={t("Application navigation")}
                initial={{ x: "-100%" }}
                animate={{ x: 0 }}
                exit={{ x: "-100%" }}
                transition={{ type: "spring", stiffness: 360, damping: 34 }}
                className="fixed bottom-0 left-0 top-14 z-40 w-[min(86vw,20rem)] overflow-hidden shadow-2xl sm:top-16 xl:hidden"
              >
                {isAdminPath ? (
                  <AdminSidebar />
                ) : (
                  <Sidebar isMobile onNavigate={() => setIsMobileSidebarOpen(false)} />
                )}
              </motion.div>
            </>
          )}
        </AnimatePresence>

        <div className={`flex min-h-0 ${!isNoSidebarPage ? "mt-14 h-[calc(100svh-3.5rem)] sm:mt-16 sm:h-[calc(100svh-4rem)]" : "h-[100svh]"}`}>
          {hasApplicationSidebar && (
            <div className="hidden h-full shrink-0 xl:flex">
              {isAdminPath ? <AdminSidebar /> : <Sidebar />}
            </div>
          )}

          <main className="flex-1 min-w-0 min-h-0 h-full overflow-hidden relative">
            {/* Responsiveness Container */}
            <div className="h-full w-full max-w-[1600px] mx-auto relative min-w-0">
              <AnimatePresence mode="wait">
                <motion.div
                  key={pathname}
                  initial={{ opacity: 0, x: 20 }}
                  animate={{ opacity: 1, x: 0 }}
                  exit={{ opacity: 0, x: -20 }}
                  transition={{ duration: 0.3, ease: "easeInOut" }}
                  className="h-full w-full overflow-y-auto overscroll-contain"
                >
                  {children}
                </motion.div>
              </AnimatePresence>
            </div>
          </main>
        </div>
      </LocaleBoundary>
      </ToastProvider>
    </div>
  );
}
